import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class SortableElementsStorageService<T extends { id: number }> {
  public readonly clonedPositions = computed<T[]>(() => this.positions().map((p) => cloneDeep(p)));

  // internal mutable signal
  private readonly _positions: WritableSignal<T[]> = signal<T[]>([]);
  // expose read-only view
  // eslint-disable-next-line @typescript-eslint/member-ordering
  readonly positions: Signal<T[]> = this._positions.asReadonly();

  public set(entries: T[]): void {
    this._positions.set([...entries]);
  }

  public addPosition(after: T, newEntry: T): void {
    this._positions.update((list) => {
      const arr = [...list];
      const idx = arr.findIndex((p) => p.id === after.id);
      const insertAt = idx < 0 ? arr.length : idx + 1;

      arr.splice(insertAt, 0, newEntry);

      // Re-number ids to stay contiguous.
      for (let i = 0; i < arr.length; i++) {
        const entry = cloneDeep(arr[i]);
        entry.id = i;
        arr[i] = entry;
      }
      return arr;
    });
  }

  public updatePosition(updated: T): void {
    this._positions.update((list) =>
      list.map((p) => (p.id === updated.id ? cloneDeep(updated) : p)),
    );
  }

  public removePosition(target: T): void {
    this._positions.update((list) => {
      const index = list.findIndex((p) => p.id === target.id);
      if (index < 0) {
        return list;
      }

      return list.toSpliced(index, 1).map((p, i) => {
        const clone = cloneDeep(p);
        clone.id = i;
        return clone;
      }); // keep ids contiguous
    });
  }
}
